import { getRelatedSankeyFlow } from './get-related-sankey-flow.js';
import { isResolvedNode } from './is-resolved-sankey-node.js';
import type { Point } from '../../core/types/point.js';
import type { SankeyHoveredShape } from '../types/sankey-hover.js';
import type {
  SankeyLayoutLink,
  SankeyLayoutModel,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';

const BISECTION_ITERATIONS = 24;

export function isPointInNode(node: SankeyLayoutNode, point: Point): boolean {
  const x0 = node.x0 ?? 0;
  const x1 = node.x1 ?? 0;
  const y0 = node.y0 ?? 0;
  const y1 = node.y1 ?? 0;

  return point.x >= x0 && point.x <= x1 && point.y >= y0 && point.y <= y1;
}

function cubicAt(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number,
): number {
  const oneMinusT = 1 - t;
  return (
    oneMinusT * oneMinusT * oneMinusT * p0 +
    3 * oneMinusT * oneMinusT * t * p1 +
    3 * oneMinusT * t * t * p2 +
    t * t * t * p3
  );
}

export function isPointInLinkRibbon(
  link: SankeyLayoutLink,
  point: Point,
): boolean {
  if (!isResolvedNode(link.source) || !isResolvedNode(link.target)) {
    return false;
  }

  const sx = link.source.x1 ?? 0;
  const sy = link.y0 ?? 0;
  const tx = link.target.x0 ?? 0;
  const ty = link.y1 ?? 0;
  const mx = (sx + tx) / 2;

  if (sx === tx || point.x < sx || point.x > tx) {
    return false;
  }

  // x(t) is monotone increasing for this control-point layout: the control
  // points are (sx, mx, mx, tx), so x'(t) = 3 (mx - sx) ((1-t)^2 + t^2) and
  // mx - sx = tx - mx > 0 (given sx < tx). That lets us bisect on t to find
  // the point on the centerline directly below/above the cursor, rather than
  // solving the cubic in closed form.
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < BISECTION_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    const x = cubicAt(sx, mx, mx, tx, mid);
    if (x < point.x) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const t = (lo + hi) / 2;

  const cy = cubicAt(sy, sy, ty, ty, t);
  const dxdt = 3 * (mx - sx) * ((1 - t) * (1 - t) + t * t);
  const dydt = 6 * t * (1 - t) * (ty - sy);
  const slope = dxdt === 0 ? 0 : dydt / dxdt;

  const halfWidth = Math.max(link.width ?? 0, 1) / 2;
  // The centerline's slope stretches the vertical footprint of a
  // perpendicular band of half-width `halfWidth`; multiplying by
  // hypot(1, slope) converts back to a vertical tolerance so the hit test
  // matches the stroked band rather than over-reporting on steep sections.
  return Math.abs(point.y - cy) <= halfWidth * Math.hypot(1, slope);
}

/**
 * Finds the sankey shape under `point`, if any.
 *
 * Nodes are tested before links: node rects are small, deliberate targets
 * that the wider ribbons converging on them would otherwise swallow. Within
 * each category, the first match in layout order wins.
 */
export function findHoveredShape(
  layout: SankeyLayoutModel,
  point: Point,
): SankeyHoveredShape | undefined {
  for (const node of layout.nodes) {
    if (isPointInNode(node, point)) {
      const { nodes: relatedNodes, links: relatedLinks } =
        getRelatedSankeyFlow(node);
      return {
        type: 'node',
        node,
        relatedNodes,
        relatedLinks,
      };
    }
  }

  for (const link of layout.links) {
    if (
      isResolvedNode(link.source) &&
      isResolvedNode(link.target) &&
      isPointInLinkRibbon(link, point)
    ) {
      return {
        type: 'link',
        link,
        source: link.source,
        target: link.target,
      };
    }
  }

  return undefined;
}
